if (!customElements.get('mfv-cart-promotion')) {
  customElements.define('mfv-cart-promotion', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.addEventListener('click', async (event) => {
        const button = event.target.closest('[data-promotion-code]');
        if (!button || button.disabled) return;
        const discount = this.closest('cart-drawer').querySelector('mfv-cart-discount');
        if (!discount || discount.busy) return;
        button.disabled = true;
        button.setAttribute('aria-busy', 'true');
        try { await discount.updateDiscount(button.dataset.promotionCode); }
        finally {
          button.disabled = false;
          button.removeAttribute('aria-busy');
        }
      }, { signal: this.controller.signal });
    }
    disconnectedCallback() { this.controller?.abort(); }
  });
}

if (!customElements.get('mfv-cart-recommendations')) {
  customElements.define('mfv-cart-recommendations', class extends HTMLElement {
    static cache = new Map();

    connectedCallback() {
      this.controller = new AbortController();
      this.track = this.querySelector('.mfv-recommendations-track');
      this.addEventListener('click', (event) => this.onClick(event), { signal: this.controller.signal });
      this.track.addEventListener('scroll', () => this.updateArrows(), { signal: this.controller.signal, passive: true });
      this.resizeObserver = new ResizeObserver(() => this.updateArrows());
      this.resizeObserver.observe(this.track);
      requestAnimationFrame(() => this.updateArrows());
      if (this.dataset.mode === 'dynamic') {
        const drawer = this.closest('cart-drawer');
        this.drawerObserver = new MutationObserver(() => {
          if (drawer.classList.contains('active')) this.loadDynamicRecommendations();
        });
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class'] });
        if (drawer.classList.contains('active')) this.loadDynamicRecommendations();
      }
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      this.drawerObserver?.disconnect();
      clearTimeout(this.deferredRender);
    }

    async fetchRecommendations(productId, intent) {
      const url = new URL(this.dataset.url, location.origin);
      url.search = new URLSearchParams({ product_id: productId, intent, limit: '10', section_id: 'mfv-cart-recommendations' });
      const key = url.toString();
      const cached = this.constructor.cache.get(key);
      if (cached && Date.now() - cached.time < 180000) return cached.html;
      const response = await fetch(url, { signal: this.controller.signal });
      if (!response.ok) throw new Error('Recommendations unavailable');
      const html = await response.text();
      // Only cache successful responses; stock and merchant pairings are refreshed regularly.
      if (this.constructor.cache.size >= 100) this.constructor.cache.delete(this.constructor.cache.keys().next().value);
      this.constructor.cache.set(key, { html, time: Date.now() });
      return html;
    }

    async collectRecommendations(productIds, intent) {
      const results = new Array(productIds.length);
      let index = 0;
      // Limit simultaneous requests while considering every distinct cart product.
      await Promise.all(Array.from({ length: Math.min(3, productIds.length) }, async () => {
        while (index < productIds.length && !this.controller.signal.aborted) {
          const current = index++;
          try {
            const html = await this.fetchRecommendations(productIds[current], intent);
            const parsed = new DOMParser().parseFromString(html, 'text/html');
            results[current] = [...parsed.querySelectorAll('.mfv-recommendation')];
          } catch {
            results[current] = [];
          }
        }
      }));
      return results;
    }

    rankRecommendations(groups, excluded) {
      const candidates = new Map();
      groups.forEach((cards = []) => cards.forEach((card, index) => {
        const id = card.dataset.productId;
        if (!id || excluded.has(id)) return;
        const candidate = candidates.get(id) || { card, score: 0 };
        candidate.score += 1 / (index + 1);
        candidates.set(id, candidate);
      }));
      return [...candidates.values()].sort((a, b) => b.score - a.score).map(({ card }) => card);
    }

    async loadDynamicRecommendations() {
      if (this.dynamicStarted) return;
      this.dynamicStarted = true;
      this.setAttribute('aria-busy', 'true');
      const productIds = [...new Set((this.dataset.productIds || '').split(',').filter(Boolean))];
      const excluded = new Set(productIds);
      const limit = Math.max(2, Math.min(10, Number(this.dataset.limit) || 6));
      const selected = [];
      const addCards = (cards, source) => cards.forEach((card) => {
        const id = card.dataset.productId;
        if (selected.length >= limit || excluded.has(id)) return;
        excluded.add(id);
        card.dataset.mfvSource = source;
        selected.push(card);
      });
      try {
        const complementary = await this.collectRecommendations(productIds, 'complementary');
        addCards(this.rankRecommendations(complementary, excluded), 'complementary');
        if (selected.length < limit && !this.controller.signal.aborted) {
          const related = await this.collectRecommendations(productIds, 'related');
          addCards(this.rankRecommendations(related, excluded), 'related');
        }
        if (!this.isConnected || this.controller.signal.aborted) return;
        addCards([...this.track.querySelectorAll('.mfv-recommendation')], 'fallback');
        const render = () => {
          if (!this.isConnected) return;
          // Keep an in-progress variant choice or keyboard interaction intact.
          if (this.contains(document.activeElement)) {
            this.deferredRender = setTimeout(render, 200);
            return;
          }
          this.track.replaceChildren(...selected);
          this.track.scrollLeft = 0;
          this.hidden = selected.length === 0;
          this.dataset.recommendationsState = 'ready';
          this.updateArrows();
        };
        render();
      } finally {
        this.removeAttribute('aria-busy');
      }
    }

    updateArrows() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      this.querySelector('[data-mfv-scroll="-1"]').disabled = this.track.scrollLeft <= 2;
      this.querySelector('[data-mfv-scroll="1"]').disabled = this.track.scrollLeft >= max - 2;
    }

    onClick(event) {
      const button = event.target.closest('button');
      if (!button || button.disabled) return;
      const card = button.closest('.mfv-recommendation');
      if (button.hasAttribute('data-mfv-scroll')) {
        const firstCard = this.track.querySelector('article');
        if (!firstCard) return;
        this.track.scrollBy({ left: Number(button.dataset.mfvScroll) * (firstCard.offsetWidth + 12), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      } else if (button.hasAttribute('data-mfv-choose')) {
        card.querySelector('.mfv-recommendation-options').hidden = false;
        button.setAttribute('aria-expanded', 'true');
        card.querySelector('select').focus();
      } else if (button.hasAttribute('data-mfv-cancel')) {
        card.querySelector('.mfv-recommendation-options').hidden = true;
        card.querySelector('[data-mfv-choose]').setAttribute('aria-expanded', 'false');
        card.querySelector('[data-mfv-choose]').focus();
      } else if (button.hasAttribute('data-mfv-add') || button.hasAttribute('data-mfv-confirm')) {
        const variant = button.dataset.mfvAdd || card.querySelector('[data-mfv-variant]').value;
        this.addProduct(variant, button);
      }
    }

    async addProduct(variant, button) {
      if (this.busy) return;
      this.busy = true;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      const error = this.querySelector('[role="alert"]');
      error.hidden = true;
      const drawer = this.closest('cart-drawer');
      const focusProduct = button.closest('[data-product-id]').dataset.productId;
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(variant), quantity: 1 }], sections: drawer.getSectionsToRender().map((section) => section.id), sections_url: location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || data.message || this.dataset.error);
        if (!data.sections?.['cart-drawer']) {
          // The add succeeded even if a section failed: refresh the drawer without adding twice.
          const sectionResponse = await fetch(`${root}cart?sections=cart-drawer,cart-icon-bubble`);
          if (!sectionResponse.ok) throw new Error(this.dataset.error);
          data.sections = await sectionResponse.json();
        }
        if (!data.sections?.['cart-drawer']) throw new Error(this.dataset.error);
        drawer.renderContents(data);
        drawer.classList.remove('is-empty');
        requestAnimationFrame(() => {
          const next = drawer.querySelector('.mfv-add') || drawer.querySelector('#CartDrawer-Checkout');
          next?.focus();
        });
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', productVariantId: variant });
        }
        drawer.dispatchEvent(new CustomEvent('mfv:recommendation-added', { bubbles: true, detail: { productId: focusProduct, variantId: variant } }));
      } catch (failure) {
        error.textContent = failure.message || this.dataset.error;
        error.hidden = false;
      } finally {
        this.busy = false;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  });
}

if (!customElements.get('mfv-cart-discount')) {
  customElements.define('mfv-cart-discount', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.querySelector('form').addEventListener('submit', (event) => {
        event.preventDefault();
        const code = this.querySelector('input').value.trim();
        if (code) this.updateDiscount(code);
      }, { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        const button = event.target.closest('[data-remove-code]');
        if (button) this.updateDiscount('', button.dataset.removeCode);
      }, { signal: this.controller.signal });
    }

    disconnectedCallback() { this.controller?.abort(); }

    async updateDiscount(code, removeCode) {
      if (this.busy) return;
      this.busy = true;
      const drawer = this.closest('cart-drawer');
      const checkout = drawer.querySelector('#CartDrawer-Checkout');
      const submit = this.querySelector('[type="submit"]');
      const input = this.querySelector('input');
      const status = this.querySelector('[role="status"]');
      const codes = (this.dataset.codes || '').split(',').filter(Boolean).filter((value) => value.toLowerCase() !== (removeCode || '').toLowerCase());
      if (code && !codes.some((value) => value.toLowerCase() === code.toLowerCase())) codes.push(code);
      submit.disabled = true;
      checkout.disabled = true;
      this.setAttribute('aria-busy', 'true');
      status.hidden = true;
      input.removeAttribute('aria-invalid');
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart/update.js`, {
          method: 'POST', headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ discount: codes.join(','), ...(drawer.querySelector('#CartDrawer-Note') ? { note: drawer.querySelector('#CartDrawer-Note').value } : {}), sections: drawer.getSectionsToRender().map((section) => section.id), sections_url: location.pathname }),
        });
        const data = await response.json();
        if (!response.ok || data.status) throw new Error(data.description || this.dataset.error);
        if (!data.sections?.['cart-drawer']) {
          const sectionsResponse = await fetch(`${root}cart?sections=cart-drawer,cart-icon-bubble`);
          if (!sectionsResponse.ok) throw new Error(this.dataset.error);
          data.sections = await sectionsResponse.json();
        }
        if (!data.sections?.['cart-drawer']) throw new Error(this.dataset.error);
        const documentCopy = new DOMParser().parseFromString(data.sections['cart-drawer'], 'text/html');
        const accepted = (documentCopy.querySelector('mfv-cart-discount')?.dataset.codes || '').split(',');
        const valid = !code || accepted.some((value) => value.toLowerCase() === code.toLowerCase());
        const message = valid ? this.dataset.success : this.dataset.invalid;
        drawer.renderContents(data);
        const replacement = drawer.querySelector('mfv-cart-discount');
        replacement.querySelector('details').open = !valid;
        const newStatus = replacement.querySelector('[role="status"]');
        newStatus.textContent = removeCode ? '' : message;
        newStatus.hidden = !!removeCode;
        newStatus.classList.toggle('is-success', valid);
        const newInput = replacement.querySelector('input');
        if (!valid) { newInput.value = code; newInput.setAttribute('aria-invalid', 'true'); }
        if (valid) drawer.querySelector('#CartDrawer-Checkout').focus({ preventScroll: true });
        else newInput.focus();
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: data });
        }
      } catch (failure) {
        status.textContent = failure.message || this.dataset.error;
        status.hidden = false;
        status.classList.remove('is-success');
      } finally {
        this.busy = false;
        submit.disabled = false;
        checkout.disabled = false;
        this.removeAttribute('aria-busy');
      }
    }
  });
}
